import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
} from '@mui/material';
import { 
  Add, 
  Search, 
  Group, 
  TrendingUp,
  Refresh,
  Download,
} from '@mui/icons-material';
import StaffList from '../components/staff/StaffList';
import { useAppDispatch } from '../hooks/redux';
import { addNotification } from '../store/slices/uiSlice';
import '../styles/minimal-padding.css';

// Enhanced Metric Card Component with forwardRef
interface MetricCardProps {
  title: string;
  value: string | number;
  bgGradient: string;
  icon: React.ReactNode;
}

const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(({
  title,
  value,
  bgGradient,
  icon,
}, ref) => (
  <Card
    ref={ref}
    className="metric-card"
    sx={{
      background: bgGradient,
      position: 'relative',
      overflow: 'hidden',
      minHeight: 140,
      borderRadius: 4,
      border: 'none',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
      color: '#FFFFFF',
      cursor: 'pointer',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
        transform: 'translateX(-100%)',
        transition: 'transform 0.6s',
      },
      '&:hover::before': {
        transform: 'translateX(100%)',
      },
    }}
  >
    <CardContent sx={{ position: 'relative', zIndex: 2, p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#FFFFFF !important',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1.5px',
              mb: 1,
              display: 'block',
              opacity: 0.9,
            }}
            style={{ color: '#FFFFFF !important' }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#FFFFFF !important',
              fontWeight: 800,
              fontSize: { xs: '1.8rem', sm: '2.2rem' },
              lineHeight: 1.2,
              mb: 1,
            }}
            style={{ color: '#FFFFFF !important' }}
          >
            {value}
          </Typography>
        </Box>
        <Avatar 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)', 
            width: 70, 
            height: 70,
            color: 'white',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ fontSize: { xs: 30, sm: 35 } }}>
            {icon}
          </Box>
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));

MetricCard.displayName = 'MetricCard';

const Staff: React.FC = () => {
  const dispatch = useAppDispatch();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      {/* Enhanced Header Section with Animation */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 3, 
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(232, 180, 184, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  color: '#5C2A30',
                  mb: 1,
                }}
              >
                Staff Management
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(92, 42, 48, 0.85)',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                }}
              >
                Manage your team and track performance
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Report" arrow>
                <IconButton 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      </Paper>

      <Fade in timeout={700} style={{ transitionDelay: '500ms' }}>
        <Card 
          sx={{ 
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
            },
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  minHeight: 64,
                  '&.Mui-selected': {
                    color: '#E8B4B8',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#E8B4B8',
                  height: 3,
                },
              }}
            >
              <Tab label="Staff Management" />
              <Tab label="Performance Analytics" />
              <Tab label="Schedule Management" />
            </Tabs>
          </Box>

          {tabValue === 0 && (
            <Box sx={{ p: 3 }}>
              <StaffList />
            </Box>
          )}

          {tabValue === 1 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: '#8B5CF6', 
                    width: 48, 
                    height: 48,
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                  }}
                >
                  <TrendingUp />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Staff Performance Analytics
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  Performance analytics dashboard coming soon...
                </Typography>
              </Box>
            </Box>
          )}

          {tabValue === 2 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: '#10B981', 
                    width: 48, 
                    height: 48,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <Group />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Staff Schedule Management
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  Schedule management interface coming soon...
                </Typography>
              </Box>
            </Box>
          )}
        </Card>
      </Fade>
    </Box>
  );
};

export default Staff;